// Path: src/App.js
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import theme from './theme';
import { ErrorBoundary } from './components/common';

// Providers
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AccountProvider } from './contexts/AccountContext';
import { ModalProvider } from './contexts/ModalContext';
import { PlanUpgradeProvider } from './contexts/PlanUpgradeContext';
import { LoaderProvider } from './contexts/LoaderContext';
import { LeadProvider } from './contexts/LeadContext';
import { LeadDetailsProvider } from './contexts/LeadDetailsContext';
import { TaskProvider } from './contexts/TaskContext';
import { CallProvider } from './contexts/CallContext';
import { ProjectProvider } from './contexts/ProjectContext';
import { DeveloperProvider } from './contexts/DeveloperContext';
import { SiteVisitProvider } from './contexts/SiteVisitContext';
import { ChannelPartnerProvider } from './contexts/ChannelPartnerContext';
import { AgentReportProvider } from './contexts/AgentReportContext';
// Layouts
import AuthLayout from './components/layout/AuthLayout';
import Layout from './components/layout/Layout';

// Auth pages
import Login from './pages/auth/Login';
import Signup from './pages/auth/Signup';
import ForgotPassword from './pages/auth/ForgotPassword';
import CreateNewPassword from './pages/auth/CreateNewPassword';
import AccountStatus from './pages/auth/AccountStatus';
import CompleteOnboarding from './pages/auth/CompleteOnboarding';
import AppDownload from './components/layout/AppDownload';
import PaymentSuccess from './pages/payment/PaymentSuccess';
import PaymentFailed from './pages/payment/PaymentFailed';
import PaymentPay from './pages/payment/PaymentPay';

// Dashboard pages
import DashboardBase from './pages/dashboard/Base';
import DashboardLeads from './pages/dashboard/DashboardLeads';
import LeadBase from './pages/leads/LeadBase';
import LeadsV2 from './pages/leads/LeadsV2';
import Settings from './pages/settings/Settings';
import { BillingAndInvoiceProvider } from './contexts/BillingAndInvoiceContext';
import CustomWebhooks from './pages/settings/CustomWebhooks';
import CustomWebhookDetail from './pages/settings/CustomWebhookDetail';
import LeadDetails from './pages/leads/lead_details_v2/LeadDetails';
import SalesReport from './pages/reports/SalesReport';
import LeadReport from './pages/reports/LeadReport';
import DealLostReport from './pages/reports/DealLostReport';
import Loader from './components/common/Loader'; // Assuming you have a Loader component
import TaskCalendar from './pages/taskcalendar/TaskCalendar';
import Calls from './pages/calls/Calls';
import CallInsight from './pages/calls/call_details/CallInsight';
import WhatsApp from './pages/whatsapp/WhatsApp';
import Instagram from './pages/instagram/Instagram';
import ProjectBase from './pages/catalogue/project/ProjectBase';
import ProjectDetails from './pages/catalogue/project/ProjectDetails';
import DeveloperList from './pages/catalogue/developer/DeveloperList';
import SiteVisitBase from './pages/sitevisits/Base';
import SiteVisitDetails from './pages/sitevisits/SiteVisitDetails';
import BookingBase from './pages/bms/BookingBase';
import ReportBase from './pages/reports/Base';
import ReportsV2Base from './pages/reports_v2/ReportsV2Base';
import MyProfile from './pages/profile/MyProfile';
import ChannelPartners from './pages/channel_partners/ChannelPartners';
import ChannelPartnerProfile from './pages/channel_partners/ChannelPartnerProfile';
import AgentReportPage from './pages/reports/AgentReportPage';
import InventoryBase from './pages/inventory/InventoryBase';
import IntegrationsBase from './pages/integrations/IntegrationsBase';
import SocialMediaAutomationPage from './pages/social-media-automation';

// Common components
import NotFoundPage from './components/common/404Page';
import ForbiddenPage from './components/common/403';
import ComingSoonPage from './components/common/501';

// Authentication guard component
const AuthGuard = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <Loader />;
  }
  
  return user ? children : <Navigate to="/auth/login" replace />;
};

// Public route guard
const PublicGuard = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <Loader />;
  }
  
  return !user ? children : <Navigate to="/dashboard" replace />;
};

// Agent login guard - allows access regardless of auth status
const AgentLoginGuard = ({ children }) => {
  const { loading } = useAuth();
  
  if (loading) {
    return <Loader />;
  }
  
  return children;
};

// Account status guard - allows access regardless of auth status
const AccountStatusGuard = ({ children }) => {
  const { loading } = useAuth();
  
  if (loading) {
    return <Loader />;
  }
  
  return children;
};

// Main App component
function App() {
  return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <AuthProvider>
            <AccountProvider>
              <ModalProvider>
                <PlanUpgradeProvider>
                  <LoaderProvider>
                    <TaskProvider>
                      <CallProvider>
                        <ProjectProvider>
                          <DeveloperProvider>
                            <ChannelPartnerProvider>
                              <AppRoutes />
                            </ChannelPartnerProvider>
                          </DeveloperProvider>
                        </ProjectProvider>
                      </CallProvider>
                    </TaskProvider>
                  </LoaderProvider>
              </PlanUpgradeProvider>
            </ModalProvider>
          </AccountProvider>
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
}

// Routes component
const AppRoutes = () => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <Loader />;
  }

  return (
    <>
      <Routes>
      {/* Authentication Routes */}
      <Route path="/auth" element={<AuthLayout />}>
        <Route path="login" element={<PublicGuard><Login /></PublicGuard>} />
        <Route path="signup" element={<PublicGuard><Signup /></PublicGuard>} />
        <Route path="forgot-password" element={<PublicGuard><ForgotPassword /></PublicGuard>} />
        <Route path="create-new-password/:uuid64/:token" element={<PublicGuard><CreateNewPassword /></PublicGuard>} />
        <Route path="complete-onboarding" element={<PublicGuard><CompleteOnboarding /></PublicGuard>} />
        <Route path="account-status/:account_id" element={<AccountStatusGuard><AccountStatus /></AccountStatusGuard>} />
      </Route>

      {/* App Download Route - Accessible regardless of auth status */}
      <Route path="/app-download" element={<AgentLoginGuard><AppDownload /></AgentLoginGuard>} />

      {/* Payment complete / success - Razorpay redirects here after payment (callback_url). Query: action, plan_name. */}
      <Route path="/payment-complete" element={<AgentLoginGuard><PaymentSuccess /></AgentLoginGuard>} />
      <Route path="/payment/success" element={<AgentLoginGuard><PaymentSuccess /></AgentLoginGuard>} />

      {/* Payment entry from email link: resolve then redirect to Razorpay or show failed (SalesGro UI) */}
      <Route path="/payment/pay" element={<AgentLoginGuard><PaymentPay /></AgentLoginGuard>} />
      {/* Payment failed */}
      <Route path="/payment/failed" element={<AgentLoginGuard><PaymentFailed /></AgentLoginGuard>} />

      {/* Protected Routes */}
      <Route path="/" element={<AuthGuard><Layout /></AuthGuard>}>
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardBase />} />
        <Route path="dashboard/leads" element={<LeadProvider><DashboardLeads /></LeadProvider>} />
        <Route path="leads" element={<LeadProvider><LeadsV2 /></LeadProvider>} />
        <Route path="leads/:id" element={
          <LeadProvider>
            <LeadDetailsProvider>
              <LeadDetails />
            </LeadDetailsProvider>
          </LeadProvider>
        } />
        <Route path="settings" element={<Settings />} />
        <Route path="integrations/*" element={<IntegrationsBase />} />
        <Route path="social-media-automation" element={<SocialMediaAutomationPage />} />
        <Route path="settings/custom-webhooks" element={<CustomWebhooks />} />
        <Route path="settings/custom-webhooks/:webhookId" element={<CustomWebhookDetail />} />
        <Route path="profile" element={<MyProfile />} />
        <Route path="reports/*" element={<ReportBase />} />
        <Route path="reports-v2/*" element={<ReportsV2Base />} />
        <Route path="calendar" element={<TaskCalendar />} />
        {/* Call routes - wrapped with CallProvider */}
        <Route path="calls" element={<Calls />} />
        <Route path="calls/:callId" element={<CallInsight />} />
        {/* WhatsApp route */}
        <Route path="whatsapp" element={<WhatsApp />} />
        <Route path="instagram" element={<Instagram />} />
        {/* Catalogue routes */}
        {/*<Route path="projects" element={<ProjectBase />} />
        <Route path="projects/:projectId" element={<ProjectDetails />} />
        <Route path="developers" element={<DeveloperList />} />
        */}
        <Route path="inventory/*" element={<InventoryBase />} />
        <Route path="site-visits" element={
          <SiteVisitProvider>
            <SiteVisitBase />
          </SiteVisitProvider>
        } />
        <Route path="site-visits/:id" element={
          <SiteVisitProvider>
            <SiteVisitDetails />
          </SiteVisitProvider>
        } />

        {/* BMS routes */}
        <Route path="bookings/*" element={<BookingBase />} />
        
        {/* Channel Partners route */}
        <Route path="channel-partners" element={<ChannelPartners />} />
        <Route path="channel-partners/:id" element={<ChannelPartnerProfile />} />
        
        {/* 403 Forbidden route */}
        <Route path="403" element={<ForbiddenPage />} />
        
        {/* Catch-all route */}
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
    </>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  // Add your authentication check here
  const isAuthenticated = true; // Replace with actual auth check
  return isAuthenticated ? children : <Navigate to="/login" />;
};

export default App;